import { computed, inject, type App, type InjectionKey, isRef, type Ref } from 'vue';
import type { LoginFormData, RegisterFormData, RegisterResponse, User } from '@/types/auth';

// API response types
interface AuthResponse {
  access_token: string;
  token_type: string;
  user?: User;
}

interface ApiResponse {
  message?: string;
  detail?: string;
  success?: boolean;
}

// This is the interface for the auth store that the main app must provide.
interface AuthStore {
  user: User | null | Ref<User | null>;
  isAuthenticated: boolean | Ref<boolean>;
  isVerified: boolean | Ref<boolean>;
  isActive: boolean | Ref<boolean>;
  isLoading: boolean | Ref<boolean>;
  error: string | null | Ref<string | null>;
  login: (credentials: LoginFormData) => Promise<AuthResponse>;
  register: (userData: RegisterFormData) => Promise<RegisterResponse>;
  logout: () => Promise<void>;
  forgotPassword: (payload: { email: string }) => Promise<ApiResponse>;
  resetPassword: (payload: { token: string; new_password: string; password?: string }) => Promise<ApiResponse>;
  verifyEmail: (token: string) => Promise<User>;
  requestVerifyToken: (email: string) => Promise<ApiResponse>;
  clearError: () => void;
  refreshSession: () => Promise<boolean>;
}

// This will be the public interface of our composable
export interface AuthService {
  user: import('vue').ComputedRef<User | null>;
  isAuthenticated: import('vue').ComputedRef<boolean>;
  isVerified: import('vue').ComputedRef<boolean>;
  isActive: import('vue').ComputedRef<boolean>;
  isLoading: import('vue').ComputedRef<boolean>;
  error: import('vue').ComputedRef<string | null>;
  login: (credentials: LoginFormData) => Promise<AuthResponse>;
  register: (userData: RegisterFormData) => Promise<RegisterResponse>;
  logout: () => Promise<void>;
  forgotPassword: (email: string) => Promise<ApiResponse>;
  resetPassword: (token: string, password: string, newPassword?: string) => Promise<ApiResponse>;
  verifyEmail: (token: string) => Promise<User>;
  requestVerifyToken: (email: string) => Promise<ApiResponse>;
  clearError: () => void;
  refreshSession: () => Promise<boolean>;
  requireAuth: () => void;
  requireVerification: () => void;
}

const AuthKey: InjectionKey<AuthService> = Symbol('AuthService');

export function useAuth(): AuthService {
  const auth = inject(AuthKey);
  if (!auth) {
    throw new Error('Auth service not provided. Did you forget to install it?');
  }
  return auth;
}

export function createAuth(store: AuthStore): AuthService & { install: (app: App) => void } {
  const authStore = store;

  // Handle both plain values and refs properly
  const user = computed(() => {
    const userValue = authStore.user;
    return isRef(userValue) ? userValue.value : userValue;
  });

  const isAuthenticated = computed(() => {
    const authValue = authStore.isAuthenticated;
    return isRef(authValue) ? authValue.value : authValue;
  });

  const isVerified = computed(() => {
    const verifiedValue = authStore.isVerified;
    return isRef(verifiedValue) ? verifiedValue.value : verifiedValue;
  });

  const isActive = computed(() => {
    const activeValue = authStore.isActive;
    return isRef(activeValue) ? activeValue.value : activeValue;
  });

  const isLoading = computed(() => {
    const loadingValue = authStore.isLoading;
    return isRef(loadingValue) ? loadingValue.value : loadingValue;
  });

  const error = computed(() => {
    const errorValue = authStore.error;
    return isRef(errorValue) ? errorValue.value : errorValue;
  });

  const login = (credentials: LoginFormData) => authStore.login(credentials);
  const register = (userData: RegisterFormData) => authStore.register(userData);
  const logout = () => authStore.logout();
  const forgotPassword = (email: string) => authStore.forgotPassword({ email });
  const resetPassword = (token: string, password: string) =>
    authStore.resetPassword({ token, new_password: password });
  const verifyEmail = (token: string) => authStore.verifyEmail(token);
  const requestVerifyToken = (email: string) => authStore.requestVerifyToken(email);
  const clearError = () => authStore.clearError();
  const refreshSession = () => authStore.refreshSession();

  const requireAuth = () => {
    if (!isAuthenticated.value) {
      throw new Error('Authentication required');
    }
  };

  const requireVerification = () => {
    requireAuth();
    if (!isVerified.value) {
      throw new Error('Email verification required');
    }
  };

  const authService: AuthService = {
    user,
    isAuthenticated,
    isVerified,
    isActive,
    isLoading,
    error,
    login,
    register,
    logout,
    forgotPassword,
    resetPassword,
    verifyEmail,
    requestVerifyToken,
    clearError,
    refreshSession,
    requireAuth,
    requireVerification,
  };

  return {
    ...authService,
    install(app: App) {
      app.provide(AuthKey, authService);
    },
  };
}
